import React, { useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import useApiRequest from './useApiRequest';
import { domain } from './Domain';
import { notify } from './ProductForm';
import { User, Mail, Phone, MessageSquare, PencilLine, Send } from 'lucide-react';
import '../css/Login.css';
import '../css/InquiryForm.css';

const InquiryForm = ({ isOpen, onClose }) => {
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [question, setQuestion] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const { postRequest } = useApiRequest();

    const isValid =
        name.trim() !== '' &&
        email.trim() !== '' &&
        phone.trim() !== '' &&
        question.trim() !== '';

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!isValid || submitting) return;

        setSubmitting(true);
        const formData = new FormData();
        formData.append('name', name.trim());
        formData.append('email', email.trim());
        formData.append('phone', phone.trim());
        formData.append('question', question.trim());

        try {
            const response = await postRequest(`${domain}/inquiries`, formData);

            if (response instanceof Error) {
                notify('Error sending the inquiry', 'error');
                return;
            }

            notify('Inquiry sent successfully', 'info');
            setName('');
            setEmail('');
            setPhone('');
            setQuestion('');
            onClose();
        } finally {
            setSubmitting(false);
        }
    };

    const handleExited = () => {
        setSubmitting(false);
    };

    return (
        <Modal
            show={isOpen}
            onHide={onClose}
            onExited={handleExited}
            centered
            contentClassName="inquiry-modal-content"
            dialogClassName="inquiry-modal-dialog"
        >
            <Modal.Header closeButton className="inquiry-modal-header">
                <Modal.Title
                    as="h2"
                    className="inquiry-modal-title"
                    id="inquiry-modal-title"
                >
                    <MessageSquare size={22} strokeWidth={2} aria-hidden />
                    <span>Send an inquiry</span>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="inquiry-modal-body">
                <p className="inquiry-modal-lead">
                    Ask us about products, stock, or pricing — we&apos;ll get back to you
                    shortly.
                </p>
                <form onSubmit={handleSubmit} noValidate aria-labelledby="inquiry-modal-title">
                    <div className="inquiry-fields">
                        <label htmlFor="inquiry-name" className="login-label">
                            Name
                        </label>
                        <div className="login-input-wrap">
                            <span className="login-input-icon" aria-hidden>
                                <User size={18} strokeWidth={2} />
                            </span>
                            <input
                                id="inquiry-name"
                                type="text"
                                className="login-input"
                                placeholder="Your full name"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                autoComplete="name"
                                required
                            />
                        </div>

                        <label htmlFor="inquiry-email" className="login-label">
                            Email
                        </label>
                        <div className="login-input-wrap">
                            <span className="login-input-icon" aria-hidden>
                                <Mail size={18} strokeWidth={2} />
                            </span>
                            <input
                                id="inquiry-email"
                                type="email"
                                className="login-input"
                                placeholder="you@example.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                autoComplete="email"
                                required
                            />
                        </div>

                        <label htmlFor="inquiry-phone" className="login-label">
                            Phone
                        </label>
                        <div className="login-input-wrap">
                            <span className="login-input-icon" aria-hidden>
                                <Phone size={18} strokeWidth={2} />
                            </span>
                            <input
                                id="inquiry-phone"
                                type="tel"
                                className="login-input"
                                placeholder="Phone number"
                                value={phone}
                                onChange={(e) => setPhone(e.target.value)}
                                autoComplete="tel"
                                required
                            />
                        </div>

                        <label htmlFor="inquiry-question" className="login-label">
                            Your question
                        </label>
                        <div className="login-input-wrap login-input-wrap--textarea">
                            <span className="login-input-icon" aria-hidden>
                                <PencilLine size={18} strokeWidth={2} />
                            </span>
                            <textarea
                                id="inquiry-question"
                                className="login-input login-input--textarea"
                                placeholder="How can we help?"
                                rows={4}
                                value={question}
                                onChange={(e) => setQuestion(e.target.value)}
                                required
                            />
                        </div>
                    </div>

                    <button
                        type="submit"
                        className="login-submit inquiry-submit"
                        disabled={!isValid || submitting}
                    >
                        <span className="login-submit-inner">
                            {submitting ? (
                                'Sending…'
                            ) : (
                                <>
                                    <Send size={18} strokeWidth={2} />
                                    Submit inquiry
                                </>
                            )}
                        </span>
                    </button>
                </form>
            </Modal.Body>
        </Modal>
    );
};

export default InquiryForm;
